import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { MapPin, Building2, Upload, X } from 'lucide-react';
import { GoogleMap, LoadScript, Marker, Autocomplete } from '@react-google-maps/api';
import { createOrganizationProfile, getMyOrganizationProfile, updateOrganizationProfile } from '../../services/authApi';
import { toast } from 'sonner';
import apiClient from '../../services/apiClient';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = {
  width: '100%',
  height: '350px',
  borderRadius: '8px'
};

const defaultCenter = {
  lat: 28.6139,
  lng: 77.2090
};

const libraries = ['places'];

export const CreateOrganizationProfile = ({ editMode = false }) => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetchingProfile, setFetchingProfile] = useState(editMode);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [services, setServices] = useState([]);
  const [serviceInput, setServiceInput] = useState('');

  const [formData, setFormData] = useState({
    organization_name: '',
    organization_type: 'school',
    contact_person_name: '',
    contact_email: user?.email || '',
    contact_phone: user?.phone || '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    about: '',
    latitude: null,
    longitude: null,
    logo_url: ''
  });

  // Fetch existing profile data in edit mode
  useEffect(() => {
    if (editMode && token) {
      fetchExistingProfile();
    }
  }, [editMode, token]);

  const fetchExistingProfile = async () => {
    try {
      setFetchingProfile(true);
      const profile = await getMyOrganizationProfile();

      // Populate form data
      setFormData({
        organization_name: profile.organization_name || '',
        organization_type: profile.organization_type || 'school',
        contact_person_name: profile.contact_person_name || '',
        contact_email: profile.contact_email || user?.email || '',
        contact_phone: profile.contact_phone || user?.phone || '',
        address: profile.address || '',
        city: profile.city || '',
        state: profile.state || '',
        pincode: profile.pincode || '',
        about: profile.about || '',
        latitude: profile.latitude || null,
        longitude: profile.longitude || null,
        logo_url: profile.logo_url || ''
      });

      // Populate services array
      if (profile.services) setServices(profile.services);

      // Set logo preview
      if (profile.logo_url) {
        setLogoPreview(profile.logo_url);
      }

      // Set map position
      if (profile.latitude && profile.longitude) {
        const position = { lat: profile.latitude, lng: profile.longitude };
        setMapCenter(position);
        setMarkerPosition(position);
      }

      toast.success('Profile loaded successfully');
    } catch (error) {
      console.log('No existing profile found:', error);
      if (editMode) {
        toast.info('No existing profile found. Create a new one.');
      }
    } finally {
      setFetchingProfile(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500 * 1024) {
        toast.error('Logo size should be less than 500KB. Please compress your image before uploading.');
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setFormData({ ...formData, logo_url: '' });
  };

  const addService = () => {
    if (serviceInput.trim() && !services.includes(serviceInput.trim())) {
      setServices([...services, serviceInput.trim()]);
      setServiceInput('');
    }
  };

  const removeService = (service) => {
    setServices(services.filter(s => s !== service));
  };

  const uploadLogo = async () => {
    if (!logoFile) return null;

    try {
      const formData = new FormData();
      formData.append('file', logoFile);

      const data = await apiClient.post('/upload/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      return data.url;
    } catch (error) {
      return logoPreview;
    }
  };

  const onMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setMarkerPosition({ lat, lng });
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const addressComponents = results[0].address_components;
        const formattedAddress = results[0].formatted_address;

        let city = '', state = '', pincode = '';

        addressComponents.forEach(component => {
          if (component.types.includes('locality')) {
            city = component.long_name;
          }
          if (component.types.includes('administrative_area_level_1')) {
            state = component.long_name;
          }
          if (component.types.includes('postal_code')) {
            pincode = component.long_name;
          }
        });

        setFormData(prev => ({
          ...prev,
          address: formattedAddress,
          city: city || prev.city,
          state: state || prev.state,
          pincode: pincode || prev.pincode
        }));
      }
    });
  }, []);

  const onAutocompleteLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };

  const onPlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();

      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setMapCenter({ lat, lng });
        setMarkerPosition({ lat, lng });

        const addressComponents = place.address_components || [];
        let city = '', state = '', pincode = '';

        addressComponents.forEach(component => {
          if (component.types.includes('locality')) {
            city = component.long_name;
          }
          if (component.types.includes('administrative_area_level_1')) {
            state = component.long_name;
          }
          if (component.types.includes('postal_code')) {
            pincode = component.long_name;
          }
        });

        setFormData(prev => ({
          ...prev,
          address: place.formatted_address || '',
          city,
          state,
          pincode,
          latitude: lat,
          longitude: lng
        }));
      }
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });
          setFormData(prev => ({
            ...prev,
            latitude: lat,
            longitude: lng
          }));

          toast.success('Location captured!');
        },
        () => {
          toast.error('Unable to fetch location');
        }
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (services.length === 0) {
      toast.error('Please add at least one service');
      return;
    }

    setLoading(true);

    try {
      let logoUrl = formData.logo_url;
      if (logoFile) {
        logoUrl = await uploadLogo();
      }

      const profileData = {
        ...formData,
        logo_url: logoUrl,
        services
      };

      if (editMode) {
        await updateOrganizationProfile(profileData);
        toast.success('Organization profile updated successfully!');
      } else {
        await createOrganizationProfile(profileData);
        toast.success('Organization profile created successfully!');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'create'} profile`);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingProfile) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'organization') {
    navigate('/dashboard');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-3 rounded-lg">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl">{editMode ? 'Edit' : 'Create'} Organization Profile</CardTitle>
                <CardDescription>{editMode ? 'Update your organization information' : 'Set up your organization on Avyudha'}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Logo Upload */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Organization Logo</h3>
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    {logoPreview ? (
                      <div className="relative">
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="w-32 h-32 rounded-lg object-cover border-4 border-blue-100"
                        />
                        <button
                          type="button"
                          onClick={removeLogo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-32 h-32 rounded-lg bg-gray-200 flex items-center justify-center">
                        <Building2 className="h-16 w-16 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="logo" className="cursor-pointer">
                      <div className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                        <Upload className="h-4 w-4" />
                        <span>Upload Logo</span>
                      </div>
                      <Input
                        id="logo"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                      />
                    </Label>
                    <p className="text-xs text-gray-500 mt-2">JPG, PNG or GIF (max 500KB)</p>
                  </div>
                </div>
              </div>

              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="organization_name">Organization Name *</Label>
                    <Input
                      id="organization_name"
                      name="organization_name"
                      value={formData.organization_name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label>Organization Type *</Label>
                    <Select value={formData.organization_type} onValueChange={(value) => setFormData({ ...formData, organization_type: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="school">School</SelectItem>
                        <SelectItem value="coaching_institute">Coaching Institute</SelectItem>
                        <SelectItem value="training_center">Training Center</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="contact_person_name">Contact Person Name *</Label>
                    <Input
                      id="contact_person_name"
                      name="contact_person_name"
                      value={formData.contact_person_name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_email">Contact Email *</Label>
                    <Input
                      id="contact_email"
                      name="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_phone">Contact Phone *</Label>
                    <Input
                      id="contact_phone"
                      name="contact_phone"
                      type="tel"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="about">About Organization *</Label>
                    <Textarea
                      id="about"
                      name="about"
                      value={formData.about}
                      onChange={handleChange}
                      placeholder="Tell us about your organization..."
                      rows={4}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Services */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Services Offered</h3>
                <div className="space-y-4">
                  <div className="flex space-x-2">
                    <Input
                      value={serviceInput}
                      onChange={(e) => setServiceInput(e.target.value)}
                      placeholder="Enter a service (e.g., JEE Preparation)"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                    />
                    <Button type="button" onClick={addService}>Add</Button>
                  </div>

                  {services.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {services.map((service, idx) => (
                        <Badge key={idx} variant="secondary" className="px-3 py-1">
                          {service}
                          <button
                            type="button"
                            onClick={() => removeService(service)}
                            className="ml-2 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Location */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Location</h3>

                <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                  <div className="space-y-4">
                    <div>
                      <Label>Search Address</Label>
                      <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={onPlaceChanged}>
                        <Input placeholder="Type to search address..." />
                      </Autocomplete>
                    </div>

                    <Button type="button" variant="outline" onClick={getCurrentLocation}>
                      <MapPin className="h-4 w-4 mr-2" />
                      Use Current Location
                    </Button>

                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      center={mapCenter}
                      zoom={13}
                      onClick={onMapClick}
                    >
                      {markerPosition && <Marker position={markerPosition} />}
                    </GoogleMap>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label htmlFor="address">Address *</Label>
                        <Input
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="state">State *</Label>
                        <Input
                          id="state"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="pincode">Pincode</Label>
                        <Input
                          id="pincode"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    {formData.latitude && formData.longitude && (
                      <p className="text-sm text-green-600">
                        Location: {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)}
                      </p>
                    )}
                  </div>
                </LoadScript>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-4 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => navigate('/dashboard')}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                  {loading ? 'Creating Profile...' : 'Create Profile'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
